import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor, type MusicVideoTextKey } from '../MusicVideoEditorContext';
import { timelineMessages } from '../messages/timelineMessages';
import { textSpans } from '../preview/previewClock';
import type { LaneProps } from './Timeline';
import { spanBox } from './timelineGeometry';
import styles from './TextLane.module.scss';

interface TextBlock {
  key: MusicVideoTextKey;
  label: string;
  startSec: number;
  endSec: number;
  on: boolean;
}

/** The text lane (design Timeline): the title, the name, the end card and the custom texts. A click selects one. */
const TextLaneComponent: React.FC<LaneProps> = ({ view }) => {
  const intl = useIntl();
  const { doc, data, songMap, selection, select } = useMusicVideoEditor();
  const { texts, settings } = doc;
  const blocks = useMemo<TextBlock[]>(() => {
    const spans = textSpans(settings, songMap.downbeats);
    const list: TextBlock[] = [
      {
        key: 'title',
        label: intl.formatMessage(timelineMessages.textTitle, {
          title: texts.title.text === '' ? data.song.title : texts.title.text,
        }),
        ...spans.title,
        on: texts.title.on,
      },
      {
        key: 'name',
        label: intl.formatMessage(timelineMessages.textName, {
          artist: texts.name.text === '' ? data.song.artistName : texts.name.text,
          title: data.song.title,
        }),
        ...spans.name,
        on: texts.name.on,
      },
    ];
    if (spans.endCard !== null) {
      list.push({
        key: 'endCard',
        label: intl.formatMessage(timelineMessages.textEndCard),
        ...spans.endCard,
        on: texts.endCard.on,
      });
    }
    texts.custom.forEach((item) => {
      list.push({ key: `custom:${item.id}`, label: item.text, startSec: item.startSec, endSec: item.endSec, on: true });
    });
    return list;
  }, [data.song.artistName, data.song.title, intl, settings, songMap.downbeats, texts]);
  const selectedKey = selection?.kind === 'text' ? selection.key : null;

  return (
    <div className={clsx(styles.lane, styles.hText)}>
      {blocks.map((block) => {
        const box = spanBox(block.startSec, block.endSec, view);
        if (box === null) {
          return null;
        }
        return (
          <button
            key={block.key}
            type="button"
            className={clsx(
              styles.textBlock,
              !block.on && styles.textBlockOff,
              block.key === selectedKey && styles.blockOn
            )}
            style={{ left: `${box.left}%`, width: `calc(${box.width}% - 2px)` }}
            aria-pressed={block.key === selectedKey}
            onClick={() => select({ kind: 'text', key: block.key })}
          >
            {block.label}
          </button>
        );
      })}
    </div>
  );
};

export const TextLane = TypedMemo(TextLaneComponent);
